import CountUp from 'react-countup'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getInputPrecision } from '@repo/money'
import type { BetType } from 'src/types/domain/BetDetails'
import SyncIcon from 'src/app/assets/icons/ico-sync-alt.svg'
import { useUIActions } from 'src/store/ui'
import type { Balance } from 'src/types/domain/Player'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import type { MarbleGame } from 'src/types/domain/Game'
import type { Marble } from 'src/types/domain/Marble'
import { roundAmount } from 'src/utils/roundAmount'
import { getSmartDecimals } from 'src/utils/displayAmount'
import { usePlayerBalance } from 'src/hooks/usePlayerBalance'
import { useGetActiveBonus } from 'src/features/inventory/store'
import { isCrashGameType } from 'src/utils/gameTypeUtils'
import BetProgressButtonOverlay from './BetProgressButtonOverlay'
import { BetInput } from './BetInput'
import { CrashAutoBetControls } from './CrashAutoBetControls'

interface AutoBet {
  isEnabled: boolean
  isActive: boolean
  toggle: () => void
  startBetting: () => void
  stopBetting: (params?: { reason?: string; scope?: string }) => void
  // Crash-specific properties (optional)
  autoCashoutEnabled?: boolean
  autoCashoutMultiplier?: number
  toggleAutoCashout?: () => void
  setAutoCashoutMultiplier?: (multiplier: number) => void
}

type Props = Readonly<{
  game: MarbleGame
  betType: BetType
  currentCurrency: string
  betAmount: string
  setBetAmount: (betAmount: string) => void
  selectedMarblesCount: number
  isPlacingBet: boolean
  bettingDisabled: boolean
  bettingDisabledInfo: string
  placeBet: (betValue: string, autoCashoutMultiplier?: number) => Promise<void>
  balance: Balance
  minBetAmount: number
  maxBetAmount: number
  setSelectedMarbles: React.Dispatch<React.SetStateAction<Marble[]>>
  setSelectedPositions: React.Dispatch<React.SetStateAction<{}>>
  autoBet: AutoBet
  setMode: React.Dispatch<React.SetStateAction<'pickWinner' | 'pickOrder'>>
  potentialWin: string
  multiplier: number
  probability: number
  chipMultipliers: number[]
}>

export default function BetPane({
  game,
  currentCurrency,
  betAmount,
  setBetAmount,
  selectedMarblesCount,
  isPlacingBet,
  bettingDisabled,
  bettingDisabledInfo,
  placeBet,
  balance,
  minBetAmount,
  maxBetAmount,
  setSelectedMarbles,
  setSelectedPositions,
  autoBet,
  setMode,
  potentialWin,
  multiplier,
  probability,
  chipMultipliers
}: Props) {
  const uiActions = useUIActions()
  const { updateBalance } = usePlayerBalance()
  const [betInputValue, setBetInputValue] = useState(betAmount)
  const [rawInputValue, setRawInputValue] = useState(betAmount)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [updatedBalance, setUpdatedBalance] = useState(false)
  const { t } = useClientSideTranslation(useGetCurrentLanguage())

  const activeBonus = useGetActiveBonus(game.id)
  const hasBonusClaimed = activeBonus !== null

  // Get input precision for current currency and format value accordingly
  const inputPrecision = useMemo(() => {
    try {
      return getInputPrecision(balance.currency)
    } catch {
      return 2 // Default to 2 decimal places
    }
  }, [balance.currency])

  const formatStakeValue = useCallback(
    (value: string | number, withThousandSeparator = false) => {
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      if (isNaN(numValue)) return '0'
      // Use toFixed to avoid floating point precision issues (e.g., 10.2 * 100 = 1019.999...)
      const rounded = parseFloat(numValue.toFixed(inputPrecision))
      if (withThousandSeparator) {
        return rounded.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: inputPrecision
        })
      }
      return rounded.toString()
    },
    [inputPrecision]
  )

  useEffect(() => {
    setBetInputValue(betAmount)
    setRawInputValue(betAmount)
  }, [betAmount])

  // Update potential win while typing (no min/max enforcement)
  useEffect(() => {
    if (!isInputFocused) return

    const value = rawInputValue.replace(/[^0-9.]/g, '')
    const numValue = parseFloat(value)

    // Update bet amount for live potential win calculation (without validation)
    if (!isNaN(numValue) && numValue > 0) {
      setBetAmount(numValue.toString())
    }
  }, [rawInputValue, isInputFocused, setBetAmount])

  const checkAndUpdateBetAmount = async (newBetAmount: number): Promise<void> => {
    if (newBetAmount > balance.amount && !updatedBalance) {
      await updateBalance()
      setUpdatedBalance(true)
    }

    updateBetAmount(newBetAmount)
  }

  const updateBetAmount = useCallback(
    (newBetAmount: number) => {
      let currentBetAmount = newBetAmount
      uiActions.clearToast()

      if (currentBetAmount < minBetAmount) {
        currentBetAmount = minBetAmount
        uiActions.forcedBetValue('decrease')
      }

      if (currentBetAmount > maxBetAmount) {
        currentBetAmount = maxBetAmount
        uiActions.forcedBetValue('increase')
      }

      if (currentBetAmount > balance.amount) {
        currentBetAmount = balance.amount
        uiActions.forcedBetValue('balance')
      }

      const roundedBetAmount = roundAmount(currentBetAmount, inputPrecision)

      setBetAmount(roundedBetAmount.toString())
      setBetInputValue(roundedBetAmount.toString())
    },
    [uiActions, minBetAmount, maxBetAmount, balance.amount, setBetAmount, inputPrecision]
  )

  const handleBetInputChange = (value: string) => {
    setBetInputValue(value)
  }

  const handleValidatedBetChange = (value: string) => {
    void checkAndUpdateBetAmount(parseFloat(value))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // For crash games with auto-cashout enabled, pass the multiplier
    // Works for both manual bets and auto-bets (like Aviator)
    const autoCashout =
      autoBet.autoCashoutEnabled && autoBet.autoCashoutMultiplier
        ? autoBet.autoCashoutMultiplier
        : undefined

    await placeBet(betInputValue, autoCashout)

    if (!autoBet.isEnabled) {
      setMode('pickWinner')
      setSelectedMarbles([])
      setSelectedPositions({})
    }

    if (autoBet.isEnabled && !autoBet.isActive) {
      autoBet.startBetting()
    }
  }

  const getButtonContent = () => {
    if (isPlacingBet || autoBet.isActive) {
      return (
        <>
          <span className="bet-text">
            {hasBonusClaimed ? t('Placing free bet...') : t('Placing bet...')}
          </span>
          <span className="loading-spinner" />
        </>
      )
    }

    if (autoBet.isEnabled && !autoBet.isActive) {
      return (
        <>
          <SyncIcon />
          <span className="bet-text">
            {hasBonusClaimed ? t('Start Auto Free bet') : t('Start Auto')}
          </span>
        </>
      )
    }

    return (
      <span className="btn-bet__inner">
        <span className="bet-text">{hasBonusClaimed ? t('Free bet') : t('Bet')}</span>
      </span>
    )
  }

  return (
    <div
      className={`bet-amount ${!bettingDisabled ? 'bet-amount__show-total-win' : ''} ${game.track.gameType === 'crash' ? 'bet-amount--crash' : ''} ${game.track.gameType === 'irl_alt' || game.track.gameType === 'duck_river' || game.track.gameType === 'snow_run' || game.track.gameType === 'social_betting' ? 'bet-amount--irl-alt' : ''}`}
    >
      <div className="bet-amount_top">
        <div
          className="bet-input-container"
          style={{
            maxHeight: hasBonusClaimed ? 0 : 52,
            opacity: hasBonusClaimed ? 0 : 1
          }}
        >
          <BetInput
            chipMultipliers={chipMultipliers}
            currentValue={betAmount}
            disabled={hasBonusClaimed}
            maxAmount={maxBetAmount}
            maxBalance={balance.amount}
            minAmount={minBetAmount}
            onChange={handleBetInputChange}
            onValidatedChange={handleValidatedBetChange}
          />
        </div>
      </div>
      <div
        className={`bet-details bet-details--two-column ${
          game.track.gameType === 'irl_alt' ||
          game.track.gameType === 'duck_river' ||
          game.track.gameType === 'snow_run' ||
          game.track.gameType === 'social_betting'
            ? 'visible'
            : !bettingDisabled
              ? 'visible'
              : ''
        } ${game.track.gameType === 'irl_alt' || game.track.gameType === 'duck_river' || game.track.gameType === 'snow_run' || game.track.gameType === 'social_betting' ? `irl-alt-details ${selectedMarblesCount > 0 ? 'has-selection' : ''}` : ''}`}
      >
        {/* Left side: Stake display/input */}
        <div className="entry entry--stake">
          <div className="stake-value">
            <input
              className="stake-input"
              disabled={hasBonusClaimed}
              onChange={(e) => {
                // Remove non-numeric chars except decimal, and ensure only one decimal point
                let value = e.target.value.replace(/[^0-9.]/g, '')
                const decimalIndex = value.indexOf('.')
                if (decimalIndex !== -1) {
                  value =
                    value.slice(0, decimalIndex + 1) +
                    value.slice(decimalIndex + 1).replace(/\./g, '')
                }
                setRawInputValue(value)
                handleBetInputChange(value)
              }}
              onFocus={() => {
                setIsInputFocused(true)
                setRawInputValue(betInputValue)
              }}
              onBlur={() => {
                setIsInputFocused(false)
                const value = rawInputValue.replace(/[^0-9.]/g, '')
                const numValue = parseFloat(value)
                // Validate on blur: enforce min/max and format value
                if (!value || isNaN(numValue) || numValue <= 0) {
                  handleValidatedBetChange(minBetAmount.toString())
                } else {
                  handleValidatedBetChange(value)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur()
                }
              }}
              type="text"
              inputMode="decimal"
              value={
                isInputFocused
                  ? rawInputValue
                  : `${currentCurrency} ${formatStakeValue(betInputValue, true)}`
              }
            />
          </div>
          <span className="label">{t('Stake')}</span>
        </div>

        {/* Right side: Win amount (hidden for crash games - win depends on cashout timing) */}
        {!isCrashGameType(game.track.gameType) && (
          <div className="entry entry--win">
            <div className="win-value">
              <span className="win-label">{currentCurrency}</span>
              <CountUp
                className="win-amount"
                decimals={
                  roundAmount(potentialWin, inputPrecision) % 1 === 0
                    ? 0
                    : getSmartDecimals(roundAmount(potentialWin, inputPrecision))
                }
                duration={0.5}
                end={roundAmount(potentialWin, inputPrecision)}
                separator=","
                preserveValue={true}
              />
            </div>

            <span className="label">
              {t('Win')}
              {/* Show multiplier inline for games that don't display it on bet options */}
              {![
                'stairpong',
                'marbles_survivor',
                'fishtank',
                'irl',
                'irl_alt',
                'duck_river',
                'snow_run',
                'social_betting',
                'crash'
              ].includes(game.track.gameType) && (
                <>
                  <span className="multiplier-label-divider"></span>
                  <span className="multiplier-label">
                    <CountUp
                      decimals={2}
                      duration={0.5}
                      end={roundAmount(multiplier)}
                      separator=","
                      start={0.0}
                    />
                    x
                  </span>
                </>
              )}
            </span>
          </div>
        )}
      </div>
      <button
        className={`btn-place-bet ${isPlacingBet ? 'loading' : ''} ${
          autoBet.isActive ? 'auto-active' : ''
        }`}
        data-tooltip-text={bettingDisabledInfo}
        disabled={isPlacingBet || bettingDisabled}
        onClick={(e) => {
          void handleSubmit(e)
        }}
        type="button"
      >
        {getButtonContent()}
        <BetProgressButtonOverlay game={game} isAutoBet={autoBet.isActive} />
      </button>

      {/* Crash game auto-bet controls with auto-cashout */}
      {isCrashGameType(game.track.gameType) && autoBet.toggleAutoCashout && (
        <CrashAutoBetControls
          isEnabled={autoBet.isEnabled}
          isActive={autoBet.isActive}
          autoCashoutEnabled={autoBet.autoCashoutEnabled ?? false}
          autoCashoutMultiplier={autoBet.autoCashoutMultiplier ?? 2.0}
          onToggle={autoBet.toggle}
          onToggleAutoCashout={autoBet.toggleAutoCashout}
          onSetAutoCashoutMultiplier={autoBet.setAutoCashoutMultiplier ?? (() => {})}
          disabled={hasBonusClaimed}
        />
      )}
    </div>
  )
}
